import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import "../../styles/view-request-detail.css";
import "../../styles/request-draft.css";
import RequestDetailItem from "./reusable/request-detail-item";
import ApiService from "../../api/apiService";
import showSwal from "../swal";
import { common } from "../../util/common";
import ImagePreviewModal from "./reusable/image-preview-modal";
import {
  convertTo12HourPlusDateFormat,
  formatToPeso,
} from "../../util/helperFunctions";

const REQUESTS_LIST_URL = "/requests?p=view-requests";

const resolveReturnUrl = (value) => {
  if (value == null) return "";

  const trimmed = String(value).trim();
  const normalized = trimmed.toLowerCase();

  if (!trimmed || normalized === "null" || normalized === "undefined") {
    return "";
  }

  return trimmed;
};

function RequestDetail({
  handleShowApproversModal,
  setApproversData,
  userDetails,
}) {
  const linkData = useLocation();
  const [isLoading, setLoading] = useState(true);
  const [requisitionData, setRequisitionData] = useState(null);
  const [isUserApprover, setUserApprover] = useState(false);
  const [returnUrl, setReturnUrl] = useState("");
  const [forTagging, setForTagging] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [unauthorized, setUnauthorized] = useState(false);

  const [isOwnRequest, setIsOwnRequest] = useState(true);
  const [cancelledRequest, setCancelledRequest] = useState(false);
  const [showCancelRequest, setShowCancelRequest] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [showApproveConfirmation, setShowApproveConfirmation] = useState(false);
  const [copiedReference, setCopiedReference] = useState(false);
  const [showLongDescription, setShowLongDescription] = useState(false);

  const handleApproveDenyRequest = async (status) => {
    const apiResponse = await ApiService.forms.approveDenyRequest(
      requisitionData?.requisition?.form_id,
      requisitionData?.requisition?.form_request_detail?.id,
      status,
    );
    const { success } = apiResponse;

    if (!success) return showSwal(common.errors.APPROVE_REQUEST_ERROR);

    window.location.reload();
  };

  const handleApproveRequest = async () => {
    if (isApproving) return;

    setShowApproveConfirmation(true);
  };

  const handleConfirmApproveRequest = async () => {
    if (isApproving) return;

    setIsApproving(true);

    const apiResponse = await ApiService.forms.approveDenyRequest(
      requisitionData?.requisition?.form_id,
      requisitionData?.requisition?.form_request_detail?.id,
      true,
    );
    const { success } = apiResponse;

    if (!success) {
      setIsApproving(false);
      setShowApproveConfirmation(false);
      return showSwal(common.errors.APPROVE_REQUEST_ERROR);
    }

    window.location.reload();
  };
  const handleTagRequest = async (status) => {
    const apiResponse = await ApiService.forms.tagRequest(
      requisitionData?.requisition?.form_id,
      requisitionData?.requisition?.form_request_detail?.id,
      status,
    );
    const { success } = apiResponse;

    if (!success) return showSwal(common.errors.TAG_REQUEST_ERROR);

    leaveRequestDetail();
  };
  const handleCancelRequest = async (status) => {
    const apiResponse = await ApiService.forms.cancelRequest(
      requisitionData?.requisition?.form_id,
      requisitionData?.requisition?.form_request_detail?.id,
      status,
    );
    const { success } = apiResponse;

    if (!success) return showSwal(common.errors.CANCEL_REQUEST_ERROR);

    leaveRequestDetail();
  };

  const leaveRequestDetail = () => {
    if (returnUrl) {
      window.location.href = returnUrl;
      return;
    }

    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    window.location.href = REQUESTS_LIST_URL;
  };

  const handleGoBack = (event) => {
    if (returnUrl) return;

    event.preventDefault();
    leaveRequestDetail();
  };

  useEffect(() => {
    const getFormRequest = async (requestType, requestLink) => {
      const response = await ApiService.forms.getFormRequestDetail(
        requestType,
        requestLink,
      );
      const { success, request } = response;
      if (!success)
        return showSwal(common.errors.GETTING_REQUESTS_DETAIL_ERROR);

      setRequisitionData(request);
      setApproversData(request?.approval);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    };
    const searchParams = new URLSearchParams(linkData.search);
    const requestType = searchParams.get("t");
    const requestLinkId = searchParams.get("r");
    const _returnUrl = searchParams.get("r_url");
    const tagging = searchParams.get("tg");
    if (
      requestType !== "" &&
      requestType !== null &&
      requestLinkId !== "" &&
      requestLinkId !== null
    ) {
      setReturnUrl(resolveReturnUrl(_returnUrl));
      setForTagging(tagging === "true");
      getFormRequest(requestType, requestLinkId);
    } else {
      setReturnUrl("");
    }
  }, [linkData.search, setApproversData]);

  useEffect(() => {
    if (userDetails && requisitionData) {
      setUserApprover(false);
      setCancelledRequest(false);
      setShowCancelRequest(false);

      const { approval, overall_approval_status, reject_status, requisition } =
        requisitionData;
      const {
        first_level_approval = [],
        second_level_approval = [],
        executive_approval_count = 0,
        initial_approval_count = 0,
      } = approval || {};

      const isFirstLevelApprover = first_level_approval.find(
        (approval) => approval.id === userDetails.id,
      );
      const isSecondLevelApprover = second_level_approval.find(
        (approval) => approval.id === userDetails.id,
      );

      if (isFirstLevelApprover || isSecondLevelApprover) {
        if (
          isFirstLevelApprover &&
          !isFirstLevelApprover.has_voted &&
          !overall_approval_status &&
          !reject_status
        ) {
          setUserApprover(true);
        }
        if (
          isSecondLevelApprover &&
          !isSecondLevelApprover.has_voted &&
          !overall_approval_status &&
          !reject_status
        ) {
          setUserApprover(true);
        }
      }

      const accountUserID = userDetails?.id;
      const requestorUserID = requisition?.user_detail?.id;
      setIsOwnRequest(accountUserID === requestorUserID);

      if (
        requisition &&
        requisition.form_request_detail &&
        requisition.form_request_detail.final_status === "Cancelled"
      ) {
        setCancelledRequest(true);
        setShowCancelRequest(false);
      } else {
        if (initial_approval_count === 0 && executive_approval_count === 0) {
          if (accountUserID === requestorUserID) {
            setShowCancelRequest(true);
          }
        }
      }
    }
  }, [userDetails, requisitionData]);

  useEffect(() => {
    const searchParams = new URLSearchParams(linkData.search);
    const requestorUserId = searchParams.get("u");

    if (requestorUserId && userDetails && userDetails.id) {
      const currentUserId = userDetails.id.toString();

      const bypassUsers = ["b38cc479-6c1f-4ede-80df-46d66402be2e"];

      const isBypassUser = bypassUsers.includes(currentUserId);

      if (requestorUserId !== currentUserId && !isBypassUser) {
        setUnauthorized(true);
      }
    }
  }, [userDetails, linkData.search]);

  const isRejected = requisitionData?.reject_status;
  const isFullyApproved = requisitionData?.overall_approval_status && !isRejected;
  const requestReferenceId =
    requisitionData?.requisition?.form_request_detail?.reference_id || "N/A";
  const requestTypeName =
    requisitionData?.requisition?.pr_request_detail?.name || "Request";

  const requisition = requisitionData?.requisition;
  const approval = requisitionData?.approval || {};
  const firstLevelApprovers = approval?.first_level_approval || [];
  const secondLevelApprovers = approval?.second_level_approval || [];

  const initialRequiredCount =
    approval?.initial_approval_needed_count ?? firstLevelApprovers.length ?? 0;
  const executiveRequiredCount =
    approval?.executive_approval_needed_count ?? secondLevelApprovers.length ?? 0;

  // The requester can edit an ePR only while it is pending and nobody has voted yet.
  const canEditRequest =
    isOwnRequest &&
    Boolean(userDetails) &&
    requisition?.form_request_detail?.request_type === "ePR" &&
    requisition?.form_request_detail?.final_status === "Pending" &&
    !isRejected &&
    ![...firstLevelApprovers, ...secondLevelApprovers].some(
      (approver) => approver?.has_voted,
    );
  const editRequestUrl = `/requests?p=new-request&f=${encodeURIComponent(
    requisition?.form_id || "",
  )}&e=${encodeURIComponent(requisition?.form_request_detail?.id || "")}`;

  const initialApprovedCount = firstLevelApprovers.filter(
    (approver) => approver?.approved,
  ).length;
  const executiveApprovedCount = secondLevelApprovers.filter(
    (approver) => approver?.approved,
  ).length;

  const getApproverStatus = (approver) => {
    if (approver?.approved) {
      return {
        label: "Approved",
        icon: "fa-check-circle",
        className: "request-status-success",
      };
    }

    if (approver?.has_voted) {
      return {
        label: "Rejected",
        icon: "fa-times-circle",
        className: "request-status-danger",
      };
    }

    return {
      label: "Pending",
      icon: "fa-clock-o",
      className: "request-status-warning",
    };
  };

  const pageStatus = useMemo(() => {
    if (cancelledRequest) {
      return {
        label: "Cancelled",
        icon: "fa-ban",
        className: "request-status-neutral",
        helperText: "This request has been cancelled.",
      };
    }

    if (isRejected) {
      return {
        label: "Rejected",
        icon: "fa-times-circle",
        className: "request-status-danger",
        helperText: "A reviewer has rejected this request.",
      };
    }

    if (isFullyApproved) {
      return {
        label: "Fully Approved",
        icon: "fa-check-circle",
        className: "request-status-success",
        helperText: "All required approvals are complete.",
      };
    }

    const totalApproved = initialApprovedCount + executiveApprovedCount;
    if (totalApproved > 0) {
      return {
        label: "Partially Approved",
        icon: "fa-hourglass-half",
        className: "request-status-warning",
        helperText: "This request is currently in approval progress.",
      };
    }

    return {
      label: "Pending",
      icon: "fa-clock-o",
      className: "request-status-warning",
      helperText: "No approvals have been completed yet.",
    };
  }, [
    cancelledRequest,
    isRejected,
    isFullyApproved,
    initialApprovedCount,
    executiveApprovedCount,
  ]);

  const currentWorkflowStage = useMemo(() => {
    if (cancelledRequest || isRejected || isFullyApproved) {
      return "Final State";
    }

    if (initialApprovedCount < initialRequiredCount) {
      return "Initial Approval";
    }

    if (executiveApprovedCount < executiveRequiredCount) {
      return "Executive Approval";
    }

    return "Final State";
  }, [
    cancelledRequest,
    isRejected,
    isFullyApproved,
    initialApprovedCount,
    initialRequiredCount,
    executiveApprovedCount,
    executiveRequiredCount,
  ]);

  const requestorName = `${requisition?.user_detail?.first_name || ""} ${
    requisition?.user_detail?.last_name || ""
  }`.trim();

  const lineItems = requisition?.pr_purchases_detail || [];
  // total_cost is already net of withholding tax.
  const withholdingTax = Number(requisition?.withholding_tax) || 0;

  const rawDescription = requisition?.description || lineItems?.[0]?.description || "";
  const rawRemarks = requisition?.remarks || lineItems?.[0]?.remarks || "";
  const descriptionText = rawDescription || "No description provided.";
  const remarksText = rawRemarks || "No remarks provided.";
  const mergedNarrative = `${descriptionText}\n\nRemarks:\n${remarksText}`;
  const isNarrativeLong = mergedNarrative.length > 280;

  const getAttachmentUrl = (attachment, filePath) => {
    const resourcesUrl = process.env.REACT_APP_ATTACHMENT_DATA_RESOURCES_URL;
    if (filePath && filePath.startsWith("http")) {
      return filePath;
    }

    if (attachment?.startsWith("http")) {
      return attachment;
    }

    return `${resourcesUrl}/${attachment}`;
  };

  const getAttachmentName = (attachmentInfo) => {
    const fromRaw =
      attachmentInfo?.original_name ||
      attachmentInfo?.filename ||
      attachmentInfo?.name ||
      attachmentInfo?.attachment ||
      attachmentInfo?.file_path ||
      "attachment";

    return fromRaw.toString().split("/").pop() || "attachment";
  };

  const getAttachmentExtension = (name = "") => {
    const nameParts = name.split(".");
    if (nameParts.length < 2) return "Unknown";
    return nameParts[nameParts.length - 1].toUpperCase();
  };

  const itemAttachmentLibrary = lineItems.flatMap((item, itemIndex) => {
    const list = [
      ...(item?.purchases_attachment_detail || []),
      ...(item?.purchases_files_detail || []),
    ];

    return list.map((attachmentInfo, attachmentIndex) => {
      const resolvedUrl = getAttachmentUrl(
        attachmentInfo?.attachment,
        attachmentInfo?.file_path,
      );
      const fileName = getAttachmentName(attachmentInfo);

      return {
        id: `${itemIndex}-${attachmentIndex}-${fileName}`,
        itemDescription: item?.description || `Line item ${itemIndex + 1}`,
        fileName,
        fileType:
          attachmentInfo?.file_type || getAttachmentExtension(fileName),
        fileSize: attachmentInfo?.file_size || "Unknown",
        url: resolvedUrl,
      };
    });
  });

  const handleCopyReference = () => {
    if (!requestReferenceId || requestReferenceId === "N/A") return;

    navigator.clipboard.writeText(requestReferenceId);
    setCopiedReference(true);

    setTimeout(() => {
      setCopiedReference(false);
    }, 1800);
  };

  const openAttachmentPreview = (url) => {
    const cleanUrl = url.split("?")[0].toLowerCase();
    const isPreviewable =
      cleanUrl.endsWith(".png") ||
      cleanUrl.endsWith(".jpg") ||
      cleanUrl.endsWith(".jpeg") ||
      cleanUrl.endsWith(".gif") ||
      cleanUrl.endsWith(".webp") ||
      cleanUrl.endsWith(".bmp") ||
      cleanUrl.endsWith(".pdf");

    if (isPreviewable) {
      setPreviewImage(url);
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getWorkflowStageState = (stage) => {
    if (cancelledRequest || isRejected) {
      if (stage === "Final State") return "blocked";
      return "completed";
    }

    if (stage === "Initial Approval") {
      if (initialApprovedCount >= initialRequiredCount) return "completed";
      return "current";
    }

    if (stage === "Executive Approval") {
      if (initialApprovedCount < initialRequiredCount) return "upcoming";
      if (executiveApprovedCount >= executiveRequiredCount) return "completed";
      return "current";
    }

    if (isFullyApproved) return "completed";
    return "upcoming";
  };

  const workflowSteps = [
    {
      key: "initial",
      name: "Initial Approval",
      detail: `${initialApprovedCount} of ${initialRequiredCount} approved`,
      state: getWorkflowStageState("Initial Approval"),
      icon: "fa-user-circle",
    },
    {
      key: "executive",
      name: "Executive Approval",
      detail: `${executiveApprovedCount} of ${executiveRequiredCount} approved`,
      state: getWorkflowStageState("Executive Approval"),
      icon: "fa-briefcase",
    },
    {
      key: "final",
      name: "Final State",
      detail: pageStatus.label,
      state: getWorkflowStageState("Final State"),
      icon: "fa-flag-checkered",
    },
  ];

  return (
    <>
      {isLoading ? (
        <div className="loader--container">
          <div className="loader--"></div>
        </div>
      ) : unauthorized ? (
        <div className="unauthorized-state">
          <div className="unauthorized-card">
            <div className="unauthorized-icon">
              <i className="fa fa-lock" aria-hidden="true"></i>
            </div>

            <span className="unauthorized-eyebrow">Access Restricted</span>
            <h1>Unauthorized Access</h1>
            <p>You do not have permission to view this request.</p>

            <div className="unauthorized-note">
              Please contact the request owner or an administrator if you
              believe this is an error.
            </div>
          </div>
        </div>
      ) : requisitionData ? (
        <div className="view-request-detail-container request-detail-shell">
          <div className="request-sticky-mobile" aria-hidden="true">
            <span className="request-sticky-mobile__id">{requestReferenceId}</span>
            <span className={`request-sticky-mobile__status ${pageStatus.className}`}>
              {pageStatus.label}
            </span>
            <span className="request-sticky-mobile__stage">{currentWorkflowStage}</span>
          </div>

          <a href={returnUrl || REQUESTS_LIST_URL} onClick={handleGoBack}>
            <div className="go-back">
              <i className="fa fa-arrow-left fa-3x" aria-hidden="true"></i>
              <span>Go Back</span>
            </div>
          </a>
          <section className="request-detail-hero" aria-labelledby="request-title">
            <div>
              <span className="request-detail-eyebrow">Request Detail</span>
              <h1 id="request-title">{requestTypeName}</h1>
              <div className="request-reference-row">
                <span className="request-reference-label">Request ID</span>
                <strong className="request-reference-value">{requestReferenceId}</strong>
                <button
                  type="button"
                  className="copy-btn"
                  onClick={handleCopyReference}
                  title="Copy request ID"
                  aria-label="Copy request ID"
                >
                  <i className="fa fa-copy" aria-hidden="true"></i>
                  <span>Copy</span>
                </button>
                {copiedReference && <span className="copied-text">Copied!</span>}
              </div>
              <p className="request-detail-subtitle">{pageStatus.helperText}</p>
            </div>
            <div className="request-detail-hero__meta">
              <span className={`request-status-badge ${pageStatus.className}`}>
                <i className={`fa ${pageStatus.icon}`} aria-hidden="true"></i>
                <span>{pageStatus.label}</span>
              </span>

              <span className="request-meta-pill">
                <i className="fa fa-bolt" aria-hidden="true"></i>
                <span>{requisition?.urgent ? "Urgent" : "Standard Priority"}</span>
              </span>

              <span className="request-meta-pill">
                <i className="fa fa-clock-o" aria-hidden="true"></i>
                <span>
                  {requisition?.createdAt
                    ? convertTo12HourPlusDateFormat(requisition?.createdAt)
                    : "No request date"}
                </span>
              </span>
            </div>
          </section>

          <section className="request-workflow" aria-labelledby="approval-progress-title">
            <div className="request-section-header">
              <div>
                <span className="request-detail-eyebrow">Approval Progress</span>
                <h2 id="approval-progress-title">Workflow Status</h2>
              </div>
              <span className="request-workflow-current">Current: {currentWorkflowStage}</span>
            </div>

            <div className="workflow-stepper">
              {workflowSteps.map((step, index) => (
                <React.Fragment key={step.key}>
                  <div className={`workflow-step workflow-step--${step.state}`}>
                    <div className="workflow-step__icon">
                      <i className={`fa ${step.icon}`} aria-hidden="true"></i>
                    </div>
                    <div className="workflow-step__content">
                      <strong>{step.name}</strong>
                      <span>{step.detail}</span>
                    </div>
                  </div>
                  {index < workflowSteps.length - 1 && (
                    <div className="workflow-stepper__connector" aria-hidden="true"></div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="workflow-approvers-grid">
              <div className="workflow-approver-section">
                <h3>Level 1 - Initial Approvers</h3>
                <p>{initialApprovedCount} approved of {initialRequiredCount} required</p>
                {firstLevelApprovers.length > 0 ? (
                  <div className="workflow-approver-list">
                    {firstLevelApprovers.map((approver, index) => {
                      const status = getApproverStatus(approver);
                      return (
                        <div className="workflow-approver-item" key={`${approver?.id || "approver"}-${index}`}>
                          <div className="workflow-approver-item__identity">
                            <span className="workflow-approver-avatar">
                              {approver?.display_name?.charAt(0)?.toUpperCase() || "A"}
                            </span>
                            <div>
                              <strong>{approver?.display_name || "Unknown Approver"}</strong>
                              <span>Initial Approval Level</span>
                            </div>
                          </div>
                          <div className="workflow-approver-item__status">
                            <span className={`request-status-badge request-status-badge--small ${status.className}`}>
                              <i className={`fa ${status.icon}`} aria-hidden="true"></i>
                              <span>{status.label}</span>
                            </span>
                            <span>
                              {approver?.date
                                ? convertTo12HourPlusDateFormat(approver?.date)
                                : "No action date"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="workflow-approver-empty">No initial approvers assigned.</div>
                )}
              </div>

              <div className="workflow-approver-section">
                <h3>Level 2 - Executive Approvers</h3>
                <p>{executiveApprovedCount} approved of {executiveRequiredCount} required</p>
                {secondLevelApprovers.length > 0 ? (
                  <div className="workflow-approver-list">
                    {secondLevelApprovers.map((approver, index) => {
                      const status = getApproverStatus(approver);
                      return (
                        <div className="workflow-approver-item" key={`${approver?.id || "executive"}-${index}`}>
                          <div className="workflow-approver-item__identity">
                            <span className="workflow-approver-avatar">
                              {approver?.display_name?.charAt(0)?.toUpperCase() || "A"}
                            </span>
                            <div>
                              <strong>{approver?.display_name || "Unknown Approver"}</strong>
                              <span>Executive Approval Level</span>
                            </div>
                          </div>
                          <div className="workflow-approver-item__status">
                            <span className={`request-status-badge request-status-badge--small ${status.className}`}>
                              <i className={`fa ${status.icon}`} aria-hidden="true"></i>
                              <span>{status.label}</span>
                            </span>
                            <span>
                              {approver?.date
                                ? convertTo12HourPlusDateFormat(approver?.date)
                                : "No action date"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="workflow-approver-empty">No executive approvers assigned.</div>
                )}
              </div>
            </div>
          </section>

          <section className="request-summary-grid" aria-label="Request Summary">
            <div className="request-summary-block">
              <h3>Requestor Details</h3>
              <dl>
                <div>
                  <dt>Name</dt>
                  <dd>{requestorName || "No requestor name"}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{requisition?.user_detail?.email_address || "No email address"}</dd>
                </div>
                <div>
                  <dt>Company</dt>
                  <dd>{requisition?.pr_company_detail?.name || "No company"}</dd>
                </div>
                <div>
                  <dt>Date Needed</dt>
                  <dd>
                    {requisition?.date_needed
                      ? convertTo12HourPlusDateFormat(requisition?.date_needed)
                      : "No date needed provided"}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="request-summary-block">
              <h3>Request Metadata</h3>
              <dl>
                <div>
                  <dt>Request Type</dt>
                  <dd>{requestTypeName}</dd>
                </div>
                <div>
                  <dt>Priority</dt>
                  <dd>{requisition?.urgent ? "Urgent" : "Standard"}</dd>
                </div>
                <div>
                  <dt>Recommended Supplier</dt>
                  <dd>{requisition?.recommended_supplier || "No supplier provided"}</dd>
                </div>
                <div>
                  <dt>Current Stage</dt>
                  <dd>{currentWorkflowStage}</dd>
                </div>
              </dl>
            </div>

            <div className="request-summary-cost">
              <span>Total Estimated Cost</span>
              <strong>{formatToPeso(requisition?.total_cost || 0)}</strong>
              <small>{lineItems.length} line item{lineItems.length === 1 ? "" : "s"}</small>
              {withholdingTax > 0 && (
                <small>
                  Subtotal{" "}
                  {formatToPeso(Number(requisition?.total_cost || 0) + withholdingTax)}{" "}
                  · Less withholding tax {formatToPeso(withholdingTax)}
                </small>
              )}
            </div>
          </section>

          <section className="request-items-section" aria-labelledby="request-items-title">
            <div className="request-section-header">
              <h2 id="request-items-title">Request Line Items</h2>
              <span className="request-items-count">{lineItems.length} items</span>
            </div>

            {lineItems.length > 0 ? (
              <div className="request-items-list">
                {lineItems.map((prDetail, index) => (
                  <RequestDetailItem
                    key={index}
                    itemIndex={index}
                    prDetail={prDetail}
                    setPreviewImage={setPreviewImage}
                  />
                ))}
              </div>
            ) : (
              <div className="request-items-empty">No line items available for this request.</div>
            )}
          </section>


          {(isUserApprover && !cancelledRequest) ||
          (forTagging && !cancelledRequest) ||
          (isOwnRequest && showCancelRequest) ||
          canEditRequest ? (
            <section className="approver--actions request-actions-footer" aria-label="Request Actions">
              {isUserApprover && !cancelledRequest && (
                <>
                  <button
                    className="action-btn approve-btn"
                    onClick={handleApproveRequest}
                    disabled={isApproving}
                    aria-busy={isApproving}
                  >
                    {isApproving ? "Approving..." : "Approve Request"}
                  </button>
                  <button
                    className="action-btn deny-btn"
                    onClick={() => handleApproveDenyRequest(false)}
                  >
                    Deny Request
                  </button>
                </>
              )}

              {forTagging && !cancelledRequest && (
                <button className="action-btn approve-btn" onClick={() => handleTagRequest(true)}>
                  Tag Request
                </button>
              )}

              {canEditRequest && (
                <a className="action-btn action-btn--edit" href={editRequestUrl}>
                  <i className="fa fa-pencil" aria-hidden="true"></i> Edit Request
                </a>
              )}

              {isOwnRequest && showCancelRequest && (
                <button className="action-btn deny-btn" onClick={() => handleCancelRequest(false)}>
                  Cancel Request
                </button>
              )}
            </section>
          ) : null}

          <ImagePreviewModal image={previewImage} onClose={() => setPreviewImage(null)} />

          {showApproveConfirmation && (
            <div
              className="approval-confirmation-modal-overlay"
              onClick={() => {
                if (!isApproving) setShowApproveConfirmation(false);
              }}
            >
              <div
                className="approval-confirmation-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="approval-confirmation-title"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="approval-confirmation-close"
                  onClick={() => setShowApproveConfirmation(false)}
                  disabled={isApproving}
                  aria-label="Close approval confirmation"
                >
                  x
                </button>

                <div className="approval-confirmation-header">
                  <div className="approval-confirmation-icon" aria-hidden="true">
                    <i className="fa fa-exclamation-triangle"></i>
                  </div>
                  <div>
                    <span className="approval-confirmation-eyebrow">Final Confirmation</span>
                    <h2 id="approval-confirmation-title">Approve this request?</h2>
                  </div>
                </div>

                <p className="approval-confirmation-description">
                  You are about to approve <strong>{requestTypeName}</strong> (
                  <strong>{requestReferenceId}</strong>).
                </p>

                <div className="approval-confirmation-warning">
                  <i className="fa fa-ban" aria-hidden="true"></i>
                  <span>This action is not reversible.</span>
                </div>

                <div className="approval-confirmation-actions">
                  <button
                    type="button"
                    className="approval-confirmation-btn approval-confirmation-cancel"
                    onClick={() => setShowApproveConfirmation(false)}
                    disabled={isApproving}
                  >
                    Keep as Pending
                  </button>
                  <button
                    type="button"
                    className="approval-confirmation-btn approval-confirmation-approve"
                    onClick={handleConfirmApproveRequest}
                    disabled={isApproving}
                    aria-busy={isApproving}
                  >
                    {isApproving ? "Approving request..." : "Yes, Approve Request"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div></div>
      )}
    </>
  );
}

export default RequestDetail;
